import {
	Box,
	Button,
	Grid,
	List,
	ListItem,
	ListItemText,
	styled,
	Tab,
	Tabs,
	TextField,
	Typography,
	IconButton as IconBtn,
	ButtonBase,
	CircularProgress,
} from '@mui/material';
import { FileUploadDiv, helperText } from '../assets/theme/Theme';
import SearchDropDown from '../components/SearchDropDown';
import { useEffect, useRef, useState } from 'react';
import {
	getAccessToken,
	getElderHomeId,
	getSummaryTemplateEnabled,
	getUserId,
	getUserName,
} from '../Utils/AuthUtil';
import {
	fetchAllSummarizers,
	fetchSummary,
	generateSummary,
	getResidentIds,
	storeSummary,
} from '../services/ElderService';
import { toast } from 'react-toastify';
import { MAIN_THEME_COLOR, WHITE } from '../assets/theme/Colors';
import Dropzone from 'react-dropzone';
import {
	BigFileIcon,
	CheckSquareIcon,
	CopyIcon,
	EditWhiteIcon,
	LayoutListIcon,
	LeftArrowIcon,
	RedTrashIcon,
	SmallFileIcon,
	SummarizerIcon,
	UploadIcon,
	UserDataIcon,
	ViewWhiteIcon,
} from '../assets/icons/Icons';
import FileContentTile from '../components/FileContentTile';
import IconButton from '../components/IconButton';
import { dateConverter, sanitizeTitle } from '../Utils/Helpers';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import { SummaryDocument } from '../components/SummaryDocument';
import MainGrid from '../components/MainGrid';
import TextWithTooltip from '../components/TextWithTooltip';
import { FEATURE_TYPES } from '../Utils/Enums';
import { getEnabledUserFeatures } from '../context/MainProvider';
import AlertBanner from '../components/AlertBanner';
import { useNavigate } from 'react-router-dom';
import { getSummarizerTemplate } from '../services/CarePlanServices';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const INITIAL_ERROR_STATE = { status: false, msg: '' };

const SUMMARY_TEMPLATES = {
	narrative: 'Narrative Summary',
	structured: 'Structured Summary',
	instruct: 'Instruction-Based',
};

const TABS = {
	rs: 'Resident Summarizer',
	cs: 'Client Summarizer',
};

function CustomTabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			{...other}>
			{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
		</div>
	);
}

function TextInputField({ value, onChange, onSubmit }) {
	return (
		<>
			<Typography
				paddingY={'5px'}
				color={'#5E5C5C'}
				fontSize={'14px'}
				fontWeight={'400'}>
				Medical Text and Report
			</Typography>
			<TextField
				fullWidth
				multiline
				rows={15}
				value={value}
				sx={{
					'& .MuiInputBase-input': {
						fontSize: '14px',
					},
				}}
				onChange={e => onChange(e.target.value)}
				placeholder="Paste your medical text here..."
			/>
		</>
	);
}

function FileUploadField({
	onSubmit,
	readTextFile,
	files,
	fileError,
	onRemoveFiles,
	isLoading,
	onRemoveAll,
}) {
	return (
		<>
			{files && files.length > 0 ? (
				<Grid
					container
					flexDirection={'column'}
					sx={{
						height: '294px',
						border: '1px dashed #D9D9D9',
						backgroundColor: '#FFFFFF',
						borderRadius: '5px',
						padding: '10px',
						marginY: '5px',
					}}>
					<Grid
						container
						spacing={1}
						sx={{
							flexGrow: 1,
							overflowY: 'auto',
						}}>
						{files.map((file, index) => (
							<Grid item xs={6} sm={6} md={4} key={index}>
								<FileContentTile
									file={file}
									onRemoveFiles={onRemoveFiles}
								/>
							</Grid>
						))}
					</Grid>
					<Grid
						container
						justifyContent="flex-end"
						sx={{ marginTop: '10px' }}>
						<Dropzone
							disabled={isLoading}
							onDrop={acceptedFiles =>
								readTextFile(acceptedFiles)
							}>
							{({ getRootProps, getInputProps }) => (
								<section>
									<div {...getRootProps()}>
										<input {...getInputProps()} />
										<Button
											startIcon={
												<SmallFileIcon
													color={MAIN_THEME_COLOR}
												/>
											}
											sx={{
												border: '2px solid #28696D',
												paddingLeft: '15px',
												paddingRight: '15px',
												color: '#28696D',
												textTransform: 'none',
												marginRight: '10px',
												display: isLoading && 'none',
											}}>
											{'Select files'}
										</Button>
									</div>
								</section>
							)}
						</Dropzone>
						<Button
							onClick={onRemoveAll}
							startIcon={<RedTrashIcon />}
							sx={{
								border: '2px solid #FF4343',
								paddingLeft: '15px',
								paddingRight: '15px',
								color: '#FF4343',
								textTransform: 'none',
								marginRight: '10px',
								display: isLoading && 'none',
							}}>
							{'Remove All'}
						</Button>
					</Grid>
				</Grid>
			) : (
				<Grid container>
					<Dropzone
						onDrop={acceptedFiles => readTextFile(acceptedFiles)}>
						{({ getRootProps, getInputProps }) => (
							<section style={{ width: '100%' }}>
								<div
									{...getRootProps()}
									style={{
										...FileUploadDiv,
										height: '294px',
										display: 'flex',
										alignItems: 'center',
										flexDirection: 'column',
										justifyContent: 'center',
										border: fileError?.status
											? '1px dashed red'
											: FileUploadDiv.border,
									}}>
									<input {...getInputProps()} />
									<UploadIcon />
									<br />
									<>
										<Typography
											fontSize={'18px'}
											fontWeight={'700'}
											color={'#7C7C7C'}>
											Drop files here or click to browse
										</Typography>
										<Typography
											fontSize={'12px'}
											color={'#B4B2B2'}>
											Support for PDF, DOCX, TXT files up
											to 20mb each
										</Typography>
									</>
								</div>
							</section>
						)}
					</Dropzone>
				</Grid>
			)}
			{fileError.status && (
				<Typography style={helperText}>{fileError.msg}</Typography>
			)}
			<Box sx={{ mb: 2 }}>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText
							primaryTypographyProps={{ fontSize: '12px' }}
							primary="• Maximum file size: 20MB per file."
						/>
					</StyledListItem>
					<StyledListItem>
						<ListItemText
							primaryTypographyProps={{ fontSize: '12px' }}
							primary="• Maximum files: 5 files per upload."
						/>
					</StyledListItem>
					<StyledListItem>
						<ListItemText
							primaryTypographyProps={{ fontSize: '12px' }}
							primary="• Supported formats: PDF, DOCX, TXT."
						/>
					</StyledListItem>
					<StyledListItem>
						<ListItemText
							primaryTypographyProps={{ fontSize: '12px' }}
							primary="• OCR will be applied to scanned documents."
						/>
					</StyledListItem>
				</List>
			</Box>
		</>
	);
}

export function SummaryTile({ data, onViewSummary }) {
	const {
		id,
		summaryType,
		summaryText,
		fileKeys,
		elder,
		summaryTemplate,
		summaryVersion,
	} = data;

	const getTitle = () => {
		if (summaryType === 'file') {
			return 'Quarterly Medical Review';
		} else {
			return 'Medication Assessment Report';
		}
	};

	function getFilename(item) {
		let filename = item.split('/')[1];

		return filename.includes('_')
			? filename.split('_').slice(1).join('_')
			: filename;
	}

	return (
		<ButtonBase
			onClick={() => onViewSummary(id)}
			sx={{
				width: '100%',
				padding: '5px',
				backgroundColor: '#FCFDFF',
				borderRadius: '6px',
				border: '0.7px #E5E7EB solid',
				display: 'flex',
			}}>
			<Grid
				container
				padding={'10px'}
				textAlign={'left'}
				flexDirection={'column'}
				borderRadius={'5px'}>
				<Grid
					container
					flexDirection={'row'}
					paddingBottom={'5px'}
					justifyContent={'space-between'}
					alignItems={'center'}>
					<Grid
						flexDirection={'row'}
						display={'flex'}
						alignItems={'center'}>
						<Typography
							fontWeight={'700'}
							fontSize={'14px'}
							margin={'5px'}
							color={'#787486'}>
							{getTitle()}
						</Typography>
						<Typography
							fontWeight={'700'}
							fontSize={'12px'}
							marginLeft={'5px'}
							color={'#AABDCE'}>
							{dateConverter(data.createdDate)}
						</Typography>
					</Grid>
					<Grid
						sx={{
							backgroundColor:
								summaryType === 'file' ? '#fde7d6' : '#EBFAF8',
							padding: '5px 10px 5px 10px',
							color:
								summaryType === 'file'
									? '#DDA01E'
									: MAIN_THEME_COLOR,
							fontSize: '11px',
							borderRadius: '8px',
						}}>
						{`${summaryType === 'file' ? 'File upload' : summaryType === 'text' ? 'Text Input' : 'No data input option'}`}
					</Grid>
				</Grid>
				{summaryType === 'file' ? (
					<Grid container gap={2}>
						{fileKeys.map((item, index) => {
							return (
								<Grid
									key={index}
									item
									xs={6}
									sm={6}
									md={5}
									sx={{
										backgroundColor: '#FBFBFB',
										flexDirection: 'row',
										justifyContent: 'center',
										alignItems: 'center',
										display: 'flex',
										borderRadius: '2px',
										paddingX: '10px',
										flexWrap: 'nowrap',
									}}>
									<Grid
										sx={{
											display: 'inline-flex',
											justifyContent: 'center',
											alignItems: 'center',
										}}>
										<SmallFileIcon color={'#5E5C5C'} />
									</Grid>
									<Typography
										sx={{
											whiteSpace: 'normal',
											wordBreak: 'break-word',
											padding: '10px',
											fontSize: '10px',
											color: '#3D4A58',
											flexGrow: 1,
										}}>
										{getFilename(item)}
									</Typography>
								</Grid>
							);
						})}
					</Grid>
				) : (
					<TextWithTooltip
						text={summaryText}
						txtLength={150}
						txtStyle={{
							whiteSpace: 'normal',
							wordBreak: 'break-word',
							padding: '10px',
							fontSize: '14px',
							color: '#3D4A58',
						}}
					/>
				)}
				<Grid
					container
					paddingY={'10px'}
					marginBottom={'15px'}
					justifyContent={'center'}>
					<Typography
						sx={{
							color: MAIN_THEME_COLOR,
							fontSize: '14px',
							fontWeight: '500',
						}}>
						{summaryTemplate}
					</Typography>
				</Grid>

				<Grid container justifyContent={'space-between'}>
					<Grid
						bgcolor={'#feefcb'}
						padding={'5px 10px 5px 10px'}
						display={'flex'}
						flexDirection={'row'}
						borderRadius={'10px'}>
						<Typography
							sx={{
								fontSize: '10px',
								color: '#28696D',
								marginLeft: '2px',
								fontWeight: '600',
							}}>
							{`Summary Type: ${summaryVersion ? summaryVersion.slice(0, 1).toUpperCase() + summaryVersion.slice(1) : 'Resident'}`}
						</Typography>
					</Grid>
					{data.elder && data.elder !== '' && (
						<Grid
							bgcolor={'#87B6B31F'}
							padding={'5px 10px 5px 10px'}
							display={'flex'}
							flexDirection={'row'}
							borderRadius={'10px'}>
							<UserDataIcon />
							<TextWithTooltip
								text={elder}
								txtLength={20}
								txtStyle={{
									fontSize: '10px',
									color: '#28696D',
									marginLeft: '2px',
								}}
							/>
						</Grid>
					)}
				</Grid>
			</Grid>
		</ButtonBase>
	);
}

function SummarizerSection({
	data,
	onEdit,
	onGeneratePdf,
	onEnableEdit,
	onCloseAll,
	onSaveSummaries,
	onGoBack,
	summaryCreatedDate = null,
	isEditable = false,
	isOnlyViewSummary = false,
	isLoading = false,
	isSummaryEmpty,
	summaryTemplate,
	elder,
	onSwitchTemplate,
	isResidentSummary,
}) {
	function covertArrayToBulletString(array) {
		return array.map(item => `• ${item}`).join('\n');
	}

	function onCopyPlan(data) {
		const value = data.map(item => `• ${item}`).join('\n');

		if (navigator.clipboard) {
			navigator.clipboard.writeText(value);
		} else {
			const tempTextArea = document.createElement('textarea');
			tempTextArea.value = value;
			document.body.appendChild(tempTextArea);
			tempTextArea.select();
			document.execCommand('copy');
			document.body.removeChild(tempTextArea);
		}
	}

	const handleKeyDown = (e, index) => {
		const valueString = e.target.value;

		if (e.key === 'Enter') {
			e.preventDefault();
			onEdit(`${valueString}\n• `, index);
		} else if (e.key === 'Backspace') {
			if (valueString.endsWith('\n• ')) {
				e.preventDefault();
				const newString = valueString.slice(0, -3); // Remove last newline + bullet + space
				onEdit(newString, index);
			} else if (valueString.endsWith('•')) {
				e.preventDefault();
				const newString = valueString.slice(0, -1); // Remove last bullet
				onEdit(newString, index);
			}
		}
	};

	const textInputStyle = {
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				border: 'none',
			},
			'&:hover fieldset': {
				border: '1px solid #e0dcdc',
			},
			'&.Mui-focused fieldset': {
				border: '1px solid #e0dcdc',
			},
			fontSize: '14px',
			color: '#324054',
		},
	};

	function onRenderSummary() {
		if (
			summaryTemplate === SUMMARY_TEMPLATES.structured ||
			summaryTemplate === SUMMARY_TEMPLATES.instruct
		) {
			return (
				<>
					{data.length > 0 &&
						data.map((value, index) => {
							return (
								<Grid
									container
									key={index}
									sx={{
										backgroundColor: '#FFFFFF',
										border: '0.4 solid #CCCCCC',
										borderRadius: '4px',
										paddingTop: '10px',
										marginY: '10px',
									}}>
									<Grid
										container
										justifyContent={'space-between'}
										alignItems={'center'}
										paddingX={'15px'}>
										<Typography
											fontSize={'16px'}
											color={MAIN_THEME_COLOR}
											fontWeight={'500'}>
											{value.title}
										</Typography>
										<IconBtn
											disableFocusRipple
											onClick={() =>
												onCopyPlan(value.bodyContent)
											}
											style={{
												padding: '0px',
											}}>
											<CopyIcon />
										</IconBtn>
									</Grid>
									<Box width={'100%'} padding={'15px'}>
										<TextField
											multiline
											fullWidth
											value={covertArrayToBulletString(
												value.bodyContent,
											)}
											onChange={e =>
												onEdit(e.target.value, index)
											}
											onKeyDown={e =>
												isEditable &&
												handleKeyDown(e, index)
											}
											variant="outlined"
											placeholder=""
											sx={textInputStyle}
											InputProps={{
												readOnly: !isEditable,
											}}
										/>
									</Box>
								</Grid>
							);
						})}
				</>
			);
		} else {
			return (
				<Grid
					container
					sx={{
						backgroundColor: '#FFFFFF',
						border: '0.4 solid #CCCCCC',
						borderRadius: '4px',
						paddingTop: '10px',
						marginY: '10px',
					}}>
					<Grid
						container
						justifyContent={'flex-end'}
						alignItems={'center'}
						padding={'15px'}>
						<IconBtn
							disableFocusRipple
							onClick={() => onCopyPlan([data])}
							style={{
								padding: '0px',
							}}>
							<CopyIcon />
						</IconBtn>
					</Grid>
					<Typography
						sx={{
							fontSize: '14px',
							color: '#324054',
							paddingX: '10px',
							paddingBottom: '10px',
						}}>
						{data}
					</Typography>
				</Grid>
			);
		}
	}

	return (
		<Grid
			sx={{
				width: '100%',
				marginLeft: '30px',
				padding: '20px',
				backgroundColor: '#FFFFFF',
				borderRadius: '10px',
				paddingBottom: '10px',
			}}>
			{isSummaryEmpty ? (
				<Grid container>
					<IconBtn disableRipple onClick={onCloseAll}>
						<LeftArrowIcon />
					</IconBtn>
					<Grid
						container
						sx={{
							backgroundColor: '#FCFDFF',
							borderRadius: '10px',
							padding: '20px',
							justifyContent: 'center',
						}}>
						<Typography
							sx={{ fontSize: '16px', fontWeight: '500' }}>
							No Summary Generated
						</Typography>
					</Grid>
				</Grid>
			) : (
				<Grid
					container
					sx={{
						backgroundColor: '#FCFDFF',
						borderRadius: '10px',
						padding: '20px',
					}}>
					<Grid
						container
						width={'100%'}
						justifyContent={'space-between'}
						alignItems={'center'}
						flexDirection={'row'}>
						<Grid
							container
							width={'50%'}
							flexDirection={'row'}
							alignItems={'center'}>
							{isOnlyViewSummary && (
								<IconBtn disableRipple onClick={onGoBack}>
									<LeftArrowIcon />
								</IconBtn>
							)}
							<BigFileIcon />
							<Grid>
								<Typography
									sx={{
										whiteSpace: 'normal',
										wordBreak: 'break-word',

										fontSize: '20px',
										color: '#324054',
									}}
									fontWeight={'500'}>
									Generated Summary
								</Typography>
							</Grid>
						</Grid>
						{!isOnlyViewSummary && (
							<Grid justifyItems={'center'}>
								{summaryTemplate !==
									SUMMARY_TEMPLATES.narrative ||
									(isResidentSummary && (
										<IconButton
											text={isEditable ? 'View' : 'Edit'}
											Icon={
												isEditable
													? ViewWhiteIcon
													: EditWhiteIcon
											}
											onClick={onEnableEdit}
										/>
									))}
								{isResidentSummary && (
									<Button
										onClick={onSwitchTemplate}
										sx={{
											backgroundColor: '#28696D',
											paddingLeft: '15px',
											paddingRight: '15px',
											color: '#FFFFFF',
											marginX: '5px',
											textTransform: 'none',
											':hover': {
												backgroundColor: '#28696D',
											},
										}}>
										{'Switch template'}
									</Button>
								)}
								<Button
									onClick={onCloseAll}
									sx={{
										border: '1px solid #28696D',
										paddingLeft: '15px',
										paddingRight: '15px',
										color: '#28696D',
										textTransform: 'none',
									}}>
									{'Generate New Summary'}
								</Button>
							</Grid>
						)}
					</Grid>
					<Grid
						container
						sx={{
							backgroundColor: '#FFFFFF',
							border: '0.4 solid #CCCCCC',
							borderRadius: '4px',
							paddingTop: '10px',
							marginY: '10px',
							flexDirection: 'column',
						}}>
						{isResidentSummary && (
							<Typography
								sx={{
									whiteSpace: 'normal',
									wordBreak: 'break-word',
									fontSize: '14px',
									color: MAIN_THEME_COLOR,
								}}
								fontWeight={'500'}>
								{`Elder: ${elder}`}
							</Typography>
						)}
						<Typography
							sx={{
								whiteSpace: 'normal',
								wordBreak: 'break-word',
								fontSize: '14px',
								color: MAIN_THEME_COLOR,
							}}
							fontWeight={'500'}>
							{`Template: ${summaryTemplate}`}
						</Typography>
						<Typography
							sx={{
								whiteSpace: 'normal',
								wordBreak: 'break-word',
								fontSize: '14px',
								color: '#BCBCBC',
							}}
							fontWeight={'500'}>
							{`Generated On: ${dateConverter(summaryCreatedDate ? summaryCreatedDate : new Date().toString())}`}
						</Typography>
					</Grid>
					{onRenderSummary()}
					{!isOnlyViewSummary && (
						<Grid
							container
							justifyContent={'flex-end'}
							alignItems={'center'}
							paddingY={'10px'}>
							{isLoading && (
								<CircularProgress
									sx={{ color: MAIN_THEME_COLOR }}
									size={'2rem'}
								/>
							)}
							{summaryTemplate !==
								SUMMARY_TEMPLATES.narrative && (
								<Button
									variant="contained"
									style={{
										backgroundColor: '#FFFFFF',
										boxShadow: 'none',
										textTransform: 'none',
										fontSize: '14px',
										fontWeight: '400',
										marginLeft: '5px',
										color: '#28696D',
										marginRight: '5px',
										border: '1px solid #28696D',
									}}
									onClick={onGeneratePdf}>
									Export as PDF
								</Button>
							)}
							<IconButton
								text={'Save'}
								onClick={onSaveSummaries}
							/>
						</Grid>
					)}
				</Grid>
			)}
		</Grid>
	);
}

function SummarizerTemplates({
	selected,
	onSelect,
	onSubmit,
	isLoading,
	onReset,
	templates,
	instruction,
	onChangeInstruction,
	isTemplateEnabled,
	summaryType,
}) {
	const Template = ({
		title,
		icon,
		description,
		isSelected,
		onSelectItem,
	}) => {
		return (
			<ButtonBase
				onClick={onSelectItem}
				sx={{
					flex: 1,
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'flex-start',
					height: 250,
					border: `3px solid ${isSelected ? '#28696D' : '#e5e7eb'}`,
					padding: '10px 20px',
					borderRadius: '8px',
					'&:hover': {
						borderColor: '#28696D',
						borderStyle: 'solid',
					},
					backgroundColor: isSelected ? '#ebfaf8' : '#FFFFFF',
					transition: 'all 0.2s ease',
					boxShadow: isSelected ? 3 : 0,
				}}>
				<Grid
					sx={{
						height: 45,
						width: 45,
						borderRadius: 50,
						justifyContent: 'center',
						alignItems: 'center',
						display: 'flex',
						marginTop: '25px',
						marginBottom: '15px',
						backgroundColor: MAIN_THEME_COLOR,
					}}>
					{icon}
				</Grid>
				<Typography
					sx={{
						fontSize: '16px',
						fontWeight: '600',
						wordBreak: 'break-word',
					}}>
					{title}
				</Typography>
				<Typography
					sx={{
						fontSize: '14px',
						fontWeight: '400',
						marginTop: '5px',
						textAlign: 'left',
						color: '#6b7280',
					}}>
					{description}
				</Typography>
			</ButtonBase>
		);
	};

	return (
		<Grid>
			<Typography
				sx={{
					fontSize: '18px',
					fontWeight: '600',
					marginBottom: '20px',
				}}>
				Choose Summary Template
			</Typography>
			<Grid container columnSpacing={3} rowSpacing={2}>
				{isTemplateEnabled &&
					templates &&
					templates.length > 0 &&
					templates.map((template, index) => (
						<Grid key={index} item xs={12} md={4}>
							<Template
								title={template.name}
								description={'Custom template'}
								icon={<SummarizerIcon color={'#FFFFFF'} />}
								isSelected={selected === template.template_key}
								onSelectItem={() =>
									onSelect(template.template_key)
								}
							/>
						</Grid>
					))}
				<Grid item xs={12} md={4}>
					<Template
						title={SUMMARY_TEMPLATES.narrative}
						description={
							'A paragraph-style summary, ideal for handovers and reports.'
						}
						icon={<SummarizerIcon color={'#FFFFFF'} />}
						isSelected={selected === SUMMARY_TEMPLATES.narrative}
						onSelectItem={() =>
							onSelect(SUMMARY_TEMPLATES.narrative)
						}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<Template
						title={SUMMARY_TEMPLATES.structured}
						description={
							'Sectioned summary with key labels for quick scanning.'
						}
						icon={<LayoutListIcon />}
						isSelected={selected === SUMMARY_TEMPLATES.structured}
						onSelectItem={() =>
							onSelect(SUMMARY_TEMPLATES.structured)
						}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<Template
						title={SUMMARY_TEMPLATES.instruct}
						description={
							'An actionable checklist of care tasks and recommendations.'
						}
						icon={<CheckSquareIcon />}
						isSelected={selected === SUMMARY_TEMPLATES.instruct}
						onSelectItem={() =>
							onSelect(SUMMARY_TEMPLATES.instruct)
						}
					/>
				</Grid>
			</Grid>
			{selected === SUMMARY_TEMPLATES.instruct && (
				<Grid marginTop={'5px'}>
					<Typography
						paddingY={'5px'}
						color={'#5E5C5C'}
						fontSize={'14px'}
						fontWeight={'400'}>
						Instruction
					</Typography>
					<TextField
						fullWidth
						multiline
						rows={10}
						value={instruction}
						sx={{
							'& .MuiInputBase-input': {
								fontSize: '14px',
							},
						}}
						onChange={e => onChangeInstruction(e.target.value)}
						placeholder="Enter your instruction..."
					/>
				</Grid>
			)}
			<Grid
				container
				justifyContent={
					summaryType === TABS.cs ? 'flex-end' : 'space-between'
				}
				alignItems={'center'}
				marginTop={'10px'}>
				{summaryType === TABS.rs && (
					<Button
						variant="contained"
						style={{
							backgroundColor: '#F3F4F6',
							boxShadow: 'none',
							marginTop: '10px',
							textTransform: 'none',
							height: '40px',
							fontSize: '14px',
							fontWeight: '500',
							color: '#374151',
						}}
						onClick={onReset}>
						Reset Inputs
					</Button>
				)}
				<Button
					variant="contained"
					disabled={selected === ''}
					style={{
						width: '20%',
						backgroundColor:
							selected === '' ? '#FFFFFF' : '#28696D',
						boxShadow: 'none',
						marginTop: '10px',
						textTransform: 'none',
						paddingTop: '8px',
						paddingBottom: '8px',
						fontSize: '14px',
						fontWeight: '400',
						border: `1px solid ${selected === '' ? '#D9D9D9' : '#28696D'} `,
					}}
					onClick={onSubmit}>
					{isLoading ? (
						<CircularProgress
							sx={{ color: '#FFFFFF' }}
							size={'1.5rem'}
						/>
					) : summaryType === TABS.cs ? (
						'Next'
					) : (
						'Generate Summary'
					)}
				</Button>
			</Grid>
		</Grid>
	);
}

function FileUploadSection({
	tabValue,
	handleChange,
	text,
	setText,
	setShowSummaryTemplates,
	txtError,
	isLoading,
	fileData,
	readTextFile,
	onRemoveFiles,
	fileError,
	setFileData,
}) {
	function a11yProps(index) {
		return {
			id: `simple - tab - ${index} `,
			'aria-controls': `simple - tabpanel - ${index} `,
		};
	}

	return (
		<Grid>
			<Typography color={'#5E5C5C'} fontSize={'16px'} fontWeight={'600'}>
				Input Data Option (Optional)
			</Typography>
			<Box sx={{ width: '100%' }}>
				<Box
					sx={{
						borderBottom: 1,
						borderColor: 'divider',
						width: '100%',
					}}>
					<StyledTabs
						sx={{
							width: '100%',
						}}
						value={tabValue}
						onChange={handleChange}>
						<StyledTab
							label="Text Input"
							sx={{
								width: '50%',
								textTransform: 'none',
							}}
							{...a11yProps(0)}
						/>
						<StyledTab
							label="File Upload"
							sx={{ width: '50%' }}
							{...a11yProps(1)}
						/>
					</StyledTabs>
				</Box>
				<CustomTabPanel value={tabValue} index={0}>
					<TextInputField
						value={text}
						onChange={value => setText(value)}
						onSubmit={() => setShowSummaryTemplates(true)}
						textError={txtError}
						isLoading={isLoading}
					/>
				</CustomTabPanel>
				<CustomTabPanel value={tabValue} index={1}>
					<FileUploadField
						files={fileData}
						readTextFile={readTextFile}
						onRemoveFiles={onRemoveFiles}
						onSubmit={() => setShowSummaryTemplates(true)}
						fileError={fileError}
						isLoading={isLoading}
						onRemoveAll={setFileData}
					/>
				</CustomTabPanel>
			</Box>
		</Grid>
	);
}

function DetailSummarizer() {
	const navigate = useNavigate();
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const isTemplateEnabled = getSummaryTemplateEnabled();
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const isThisFeatureDisabled = !enabledUserFeatureList.includes(
		FEATURE_TYPES[4].key,
	);
	const [id, setId] = useState(null);
	const [viewType, setViewType] = useState(null);
	const [residentIds, setResidentIds] = useState([]);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [tabValue, setTabValue] = useState(0);
	const [text, setText] = useState('');
	const [fileData, setFileData] = useState([]);
	const [txtError, setTxtError] = useState(INITIAL_ERROR_STATE);
	const [fileError, setFileError] = useState(INITIAL_ERROR_STATE);
	const [summaryData, setSummaryData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSummaryGenerated, setIsSummaryGenerated] = useState(false);
	const [summarizedData, setSummarizedData] = useState([]);
	const [isEditing, setIsEditing] = useState(false);
	const [singleSummary, setSingleSummary] = useState({});
	const [isStoringData, setIsStoringData] = useState(false);
	const [isSummaryEmpty, setIsSummaryEmpty] = useState(false);
	const [scroll, setScroll] = useState(false);
	const [showSummaryTemplates, setShowSummaryTemplates] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState('');
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [instruction, setInstruction] = useState('');
	const [templates, setTemplates] = useState([]);
	const [residentSummaryTemplates, setResidentSummaryTemplates] = useState(
		[],
	);
	const [clientSummaryTemplates, setClientSummaryTemplates] = useState([]);
	const [summaryTypeTab, setSummaryTypeTab] = useState(TABS.rs);
	const [onShowClientSummaryInputs, setOnShowClientSummaryInputs] =
		useState(false);
	const [summaryEndpoints, setSummaryEndpoints] = useState([]);

	useEffect(() => {
		fetchResidentIds();
		fetchSummarizersData();
		fetchSummarizerTemplateFiles(true);
	}, []);

	async function fetchResidentIds() {
		const response = await getResidentIds(homeId, token);

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);
			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
			setShowNoResidentBanner(response.content.length > 0 ? false : true);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchSummarizersData() {
		const response = await fetchAllSummarizers(homeId, token);

		if (!response.error) {
			setSummaryData(response);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchSummarizerTemplateFiles(isInitialFetch = false) {
		const cResponse = await getSummarizerTemplate(homeId, 'client');
		if (!cResponse.error) {
			const temp = cResponse.templates.filter(
				item =>
					![
						'narrative_summary',
						'structured_summary',
						'instruction_based_summary',
					].includes(item.template_key),
			);
			setClientSummaryTemplates(temp);
			isInitialFetch && setTemplates(temp);
		}
		const eResponse = await getSummarizerTemplate(homeId, 'elder');
		if (!eResponse.error) {
			const temp = eResponse.templates.filter(
				item =>
					![
						'narrative_summary',
						'structured_summary',
						'instruction_based_summary',
					].includes(item.template_key),
			);
			setResidentSummaryTemplates(temp);
		}
	}

	function handleChange(event, newValue) {
		setTabValue(newValue);
	}

	function readTextFile(e) {
		if (fileData.length === 5) {
			setFileError({
				status: true,
				msg: 'Maximum files: 5 files per upload.',
			});

			return;
		}

		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx'].includes(ext)) {
			setFileError({
				status: true,
				msg: 'Invalid file format',
			});
			return;
		}

		if (e[0] && e[0].size === 0) {
			setFileError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		setFileError(INITIAL_ERROR_STATE);
		setFileData([...fileData, e[0]]);
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setFileData(clone);
	}

	function validateInputs() {
		let isValid = true;

		if (tabValue === 0) {
			if (!text || text === '') {
				setTxtError({ status: true, msg: 'Enter medical information' });
				isValid = false;
			}
		} else {
			if (fileData.length === 0) {
				setFileError({ status: true, msg: 'Upload the files' });
				isValid = false;
			}
		}

		return isValid;
	}

	function getTemplateKey(value) {
		if (value === SUMMARY_TEMPLATES.narrative) {
			return 'narrative_summary';
		} else if (value === SUMMARY_TEMPLATES.instruct) {
			return 'instruction_based_summary';
		} else if (value === SUMMARY_TEMPLATES.structured) {
			return 'structured_summary';
		} else {
			return value;
		}
	}

	async function onGenerateSummary() {
		setIsLoading(true);
		const isClientSummary = summaryTypeTab === TABS.cs;

		const formData = new FormData();
		formData.append('client_id', getElderHomeId());
		formData.append('base_url', central_db_host);
		formData.append('auth_token', token);

		formData.append(
			isClientSummary ? 'template_key' : 'template_type',
			getTemplateKey(selectedTemplate),
		);
		if (
			selectedTemplate === SUMMARY_TEMPLATES.instruct &&
			instruction &&
			instruction !== ''
		) {
			formData.append('instructions', instruction);
		}

		if (id && id !== '' && !isClientSummary) {
			const selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0] || item.name === id,
			);

			formData.append(
				'elder_id',
				selectedElder.isFromExternalApi
					? selectedElder.elderId
					: selectedElder.id,
			);
			formData.append('use_api', selectedElder.isFromExternalApi);
		}

		if (tabValue === 0 && text !== '') {
			formData.append('input_text', text);
		} else if (tabValue === 1 && fileData.length !== 0) {
			fileData.forEach(file => {
				formData.append(
					isClientSummary ? 'template_file' : 'uploaded_files',
					new Blob([file], { type: 'text/plain' }),
					file.name,
				);
			});
		}

		if (
			isClientSummary &&
			summaryEndpoints &&
			summaryEndpoints.length > 0
		) {
			summaryEndpoints.forEach(url => {
				formData.append('client_summary_endpoints', url);
			});
		}

		const route = isClientSummary ? 'summarise_client' : 'summarise_elder';
		const responseData = await generateSummary(formData, route);

		if (!responseData.error) {
			if (isClientSummary) {
				const summary = responseData.summary_text;
				if (summary.length !== 0) {
					setIsSummaryEmpty(false);
					setSummarizedData(summary);
				} else {
					setIsSummaryEmpty(true);
				}
			} else {
				if (selectedTemplate === SUMMARY_TEMPLATES.narrative) {
					const summary = responseData.summarised_information.summary;
					if (summary.length !== 0) {
						setIsSummaryEmpty(false);
						setSummarizedData(summary);
					} else {
						setIsSummaryEmpty(true);
					}
				} else {
					const summary = responseData.summarised_information;
					if (Object.keys(summary).length !== 0) {
						const formattedSummary = Object.keys(summary)
							.filter(
								value =>
									![
										'Elder_ID',
										'Generated_On',
										'Template',
									].includes(value) && value.length > 0,
							)
							.map(item => {
								const bodyContent = checkValueTypes(
									summary[item],
								);
								if (bodyContent.length > 0) {
									return {
										title: sanitizeTitle(item),
										bodyContent: bodyContent,
									};
								}

								return null;
							})
							.filter(item => item !== null);

						setIsSummaryEmpty(false);
						setSummarizedData(formattedSummary);
					} else {
						setIsSummaryEmpty(true);
					}
				}
			}
			setIsSummaryGenerated(true);
			setShowSummaryTemplates(false);
		} else {
			toast.error(responseData.message);
		}

		setScroll(true);
		setIsLoading(false);
		setTimeout(() => {
			setScroll(false);
		}, 2000);
	}

	function checkValueTypes(item) {
		if (
			Array.isArray(item) &&
			item.every(value => typeof value === 'object')
		) {
			const mergedArray = item.flatMap(obj =>
				Object.entries(obj).map(([key, value]) => `${key}: ${value} `),
			);

			return mergedArray;
		} else if (
			item !== null &&
			typeof item === 'object' &&
			!Array.isArray(item)
		) {
			const mergedArray = [];

			for (const [key, value] of Object.entries(item)) {
				if (
					typeof value === 'object' &&
					value !== null &&
					!Array.isArray(value)
				) {
					const nested = checkValueTypes(value);
					nested.forEach(entry => {
						mergedArray.push(`${entry} `);
					});
				} else {
					mergedArray.push(`${key}: ${value} `);
				}
			}

			return mergedArray;
		} else if (typeof item === 'string') {
			return [item];
		} else {
			return item;
		}
	}

	function onEnableEdit() {
		setIsEditing(!isEditing);
	}

	async function onGeneratePdf() {
		const fileName = `SenzCare.AI_Summary_${id ? id : uuidv4()}.pdf`;
		const blob = await pdf(
			<SummaryDocument
				documentData={
					summarizedData.length > 0
						? summarizedData
						: singleSummary.summaries
				}
				title={'Generated Medical Summary'}
			/>,
		).toBlob();
		saveAs(blob, fileName);
	}

	async function onSaveSummaries() {
		setIsStoringData(true);
		let selectedElderId = '';
		let elderData = '';
		if (id && id !== '') {
			const selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0] || item.name === id,
			);

			selectedElderId = selectedElder.id;
			elderData = `${selectedElder.elderId}: ${selectedElder.name} `;
		}

		const summaryType =
			fileData && fileData.length > 0 && tabValue === 1
				? 'file'
				: text && text !== '' && tabValue === 0
					? 'text'
					: 'none';

		const isClientSummary = summaryTypeTab === TABS.cs;
		const summaryNewData = {
			elderHomeId: homeId,
			elderId: selectedElderId,
			elder: elderData,
			generatedUserId: getUserId(),
			generatedUserName: getUserName(),
			summaryText: text,
			summaryType: summaryType,
			summaries:
				selectedTemplate === SUMMARY_TEMPLATES.narrative ||
				isClientSummary
					? [{ paragraph: summarizedData }]
					: summarizedData,
			summaryTemplate: selectedTemplate,
			summaryVersion: isClientSummary ? 'client' : 'resident',
		};

		const formData = new FormData();
		formData.append(
			'summary',
			new Blob([JSON.stringify(summaryNewData)], {
				type: 'application/json',
			}),
		);

		if (tabValue === 1) {
			fileData.forEach(file => {
				formData.append('file', file);
			});
		}

		const response = await storeSummary(formData, homeId, token);

		if (!response.error) {
			toast.success('Summary stored successfully');
			onCloseAll();
			fetchSummarizersData();
		} else {
			toast.error(response.message);
		}
		setIsStoringData(false);
	}

	function onCloseAll() {
		setId(null);
		setIsSummaryGenerated(false);
		setText('');
		setFileData([]);
		setSummarizedData([]);
		setSelectedTemplate('');
		setInstruction('');
		setSummaryTypeTab(TABS.rs);
		setOnShowClientSummaryInputs(false);
		setFileError({ status: false, msg: '' });
	}

	function convertBulletStringToArray(str) {
		return str.split('\n').map(line => line.replace(/^•\s*/, ''));
	}

	function handleSummaryChange(value, mainIndex) {
		const updatedItems = convertBulletStringToArray(value);
		const clone = summarizedData.map((item, index) => {
			if (mainIndex === index) {
				return {
					...item,
					bodyContent: updatedItems,
				};
			} else {
				return item;
			}
		});

		setSummarizedData(clone);
	}

	async function onFetchSummary(id) {
		const token = getAccessToken();
		const response = await fetchSummary(id, token);

		if (response.message === 'Success') {
			const data = response.content;

			if (data.summaries[0].paragraph) {
				setSelectedTemplate(SUMMARY_TEMPLATES.narrative);
				const updatedData = {
					...data,
					summaries: data.summaries[0].paragraph,
				};

				setSingleSummary(updatedData);
			} else {
				setSingleSummary(data);
				setSelectedTemplate(data.summaryTemplate);
			}
			setViewType('FullSummary');
			setId(data.elder);
		} else {
			toast.error(response.message);
		}
	}

	function onCheckValueSelected() {
		if (!id && summaryTypeTab !== TABS.cs) {
			if (tabValue === 0 && text === '') {
				return true;
			}

			if (tabValue === 1 && fileData.length == 0) {
				return true;
			}
		}

		return false;
	}

	async function onSelect(item) {
		setTemplates(
			item === TABS.cs
				? clientSummaryTemplates
				: residentSummaryTemplates,
		);
		setFileData([]);
		setText('');
		setTxtError({ status: false, msg: '' });
		setSummaryTypeTab(item);
		setOnShowClientSummaryInputs(false);
		setSelectedTemplate('');
		setFileError({ status: false, msg: '' });
		setId(null);
	}

	function onRenderSummaryTypeContent() {
		if (summaryTypeTab === TABS.cs && !showSummaryTemplates) {
			if (onShowClientSummaryInputs) {
				return (
					<>
						<FileUploadSection
							tabValue={tabValue}
							handleChange={handleChange}
							text={text}
							setText={setText}
							setShowSummaryTemplates={setShowSummaryTemplates}
							txtError={txtError}
							isLoading={isLoading}
							fileData={fileData}
							readTextFile={readTextFile}
							onRemoveFiles={onRemoveFiles}
							fileError={fileError}
							setFileData={() => setFileData([])}
						/>
						{!showSummaryTemplates && (
							<Grid container justifyContent={'flex-end'}>
								<Button
									variant="contained"
									disabled={onCheckValueSelected()}
									style={{
										width: '50%',
										backgroundColor: onCheckValueSelected()
											? '#FFFFFF'
											: '#28696D',
										boxShadow: 'none',
										marginTop: '10px',
										textTransform: 'none',
										fontSize: '14px',
										fontWeight: '400',
										paddingTop: '8px',
										paddingBottom: '8px',
										border: `1px solid ${onCheckValueSelected() ? '#D9D9D9' : '#28696D'}`,
									}}
									onClick={onGenerateSummary}>
									{isLoading ? (
										<CircularProgress
											sx={{ color: '#FFFFFF' }}
											size={'1.5rem'}
										/>
									) : (
										'Generate Summary'
									)}
								</Button>
							</Grid>
						)}
					</>
				);
			} else {
				return (
					<>
						<SummarizerTemplates
							onSelect={selected => {
								setSelectedTemplate(selected);
								const temp = templates.find(
									i => i.template_key === selected,
								);
								setSummaryEndpoints(
									temp?.selected_endpoints_summariser,
								);
								setInstruction('');
							}}
							selected={selectedTemplate}
							onSubmit={() => setOnShowClientSummaryInputs(true)}
							isLoading={isLoading}
							templates={templates}
							instruction={instruction}
							onChangeInstruction={setInstruction}
							isTemplateEnabled={isTemplateEnabled}
							summaryType={summaryTypeTab}
						/>
					</>
				);
			}
		} else if (summaryTypeTab === TABS.rs && !showSummaryTemplates) {
			return (
				<>
					<Grid sx={{ paddingBottom: '20px' }}>
						<Typography
							color={'#5E5C5C'}
							fontSize={'16px'}
							fontWeight={'600'}>
							Resident Mapping
						</Typography>
						<Grid paddingTop={'20px'} paddingBottom={'5px'}>
							<Typography
								fontSize={'16px'}
								color={'#7C7C7C'}
								paddingBottom="10px">
								Resident
							</Typography>
							<SearchDropDown
								value={id}
								dropDownData={residentIds}
								onChange={(e, value) => {
									setId(value);
								}}
								placeholder={'No Resident (Standalone)'}
							/>
						</Grid>
					</Grid>
					<FileUploadSection
						tabValue={tabValue}
						handleChange={handleChange}
						text={text}
						setText={setText}
						setShowSummaryTemplates={setShowSummaryTemplates}
						txtError={txtError}
						isLoading={isLoading}
						fileData={fileData}
						readTextFile={readTextFile}
						onRemoveFiles={onRemoveFiles}
						fileError={fileError}
						setFileData={() => setFileData([])}
					/>
					{!showSummaryTemplates && (
						<Grid container justifyContent={'flex-end'}>
							<Button
								variant="contained"
								disabled={onCheckValueSelected()}
								style={{
									width: '50%',
									backgroundColor: onCheckValueSelected()
										? '#FFFFFF'
										: '#28696D',
									boxShadow: 'none',
									marginTop: '10px',
									textTransform: 'none',
									fontSize: '14px',
									fontWeight: '400',
									paddingTop: '8px',
									paddingBottom: '8px',
									border: `1px solid ${onCheckValueSelected() ? '#D9D9D9' : '#28696D'}`,
								}}
								onClick={() => {
									setShowSummaryTemplates(true);
									setIsSummaryGenerated(false);
									setSummarizedData([]);
								}}>
								{'Next (Choose Template)'}
							</Button>
						</Grid>
					)}
				</>
			);
		}
	}

	if (viewType === 'FullSummary') {
		return (
			<MainGrid>
				<SummarizerSection
					data={singleSummary.summaries}
					onEdit={handleSummaryChange}
					isOnlyViewSummary={true}
					onGeneratePdf={onGeneratePdf}
					onEnableEdit={onEnableEdit}
					onCloseAll={onCloseAll}
					onSaveSummaries={onSaveSummaries}
					summaryCreatedDate={singleSummary.createdDate}
					onGoBack={() => {
						setViewType(null);
						setSelectedTemplate(SUMMARY_TEMPLATES.narrative);
						onCloseAll();
					}}
					summaryTemplate={selectedTemplate}
					elder={id}
				/>
			</MainGrid>
		);
	} else {
		return (
			<MainGrid>
				<Grid container paddingLeft="30px" paddingBottom={'20px'}>
					<Typography variant="h6" fontWeight={'600'} color="#28696D">
						Detail Summarizer
					</Typography>
				</Grid>
				{showNoResidentBanner && (
					<Grid
						container
						sx={{
							borderRadius: '5px',
							paddingLeft: '30px',
							paddingBottom: '10px',
						}}>
						<AlertBanner
							content={
								'No residents available. Add residents to get started.'
							}
							btnOneTxt={'Manual Onboarding'}
							btnTwoTxt={'Connect Api'}
							handleButtonOneClick={() =>
								navigate('/addResident')
							}
							handleButtonTwoClick={() =>
								navigate('/integration')
							}
							displayBtnOne={true}
							displayBtnTwo={true}
						/>
					</Grid>
				)}
				{!isSummaryGenerated ? (
					<Grid
						sx={{
							width: '100%',
							marginLeft: '30px',
							padding: '20px',
							backgroundColor: '#FFFFFF',
							borderRadius: '10px',
							paddingBottom: '10px',
							display: 'flex',
						}}
						style={
							isThisFeatureDisabled
								? {
										filter: 'blur(0.8px)',
										pointerEvents: 'none',
									}
								: {}
						}
						columnGap={2}>
						<Grid
							item
							xs={12}
							md={showSummaryTemplates ? 12 : 7}
							sx={{
								backgroundColor: '#FCFDFF',
								borderRadius: '10px',
								padding: '20px',
							}}>
							{showSummaryTemplates ? (
								<SummarizerTemplates
									onSelect={selected => {
										setSelectedTemplate(selected);
										setInstruction('');
									}}
									selected={selectedTemplate}
									onSubmit={onGenerateSummary}
									onReset={() => {
										setShowSummaryTemplates(false);
										onCloseAll();
									}}
									isLoading={isLoading}
									templates={templates}
									instruction={instruction}
									onChangeInstruction={setInstruction}
									isTemplateEnabled={isTemplateEnabled}
									summaryType={summaryTypeTab}
								/>
							) : (
								<>
									<Grid
										container
										sx={{
											backgroundColor: '#FFFFFF',
											borderRadius: '5px',
											marginBottom: '10px',
										}}>
										<Grid container flexDirection={'row'}>
											{Object.values(TABS).map(
												(item, index) => (
													<ButtonBase
														key={index}
														onClick={() =>
															onSelect(item)
														}
														sx={{
															width: '50%',
															height: 38,
															backgroundColor:
																item ==
																summaryTypeTab
																	? '#F9F8F9'
																	: WHITE,
															padding:
																'5px 24px 5px 24px',
															borderBottom:
																item ==
																summaryTypeTab
																	? `2px solid ${MAIN_THEME_COLOR}`
																	: '',
														}}>
														<Typography
															fontSize={'14px'}
															fontWeight={'500'}
															color={
																item ==
																summaryTypeTab
																	? MAIN_THEME_COLOR
																	: '#686868'
															}>
															{item}
														</Typography>
													</ButtonBase>
												),
											)}
										</Grid>
									</Grid>
								</>
							)}
							{onRenderSummaryTypeContent()}
						</Grid>
						{!showSummaryTemplates && (
							<Grid
								item
								xs={12}
								md={5}
								sx={{
									backgroundColor: '#FCFDFF',
									borderRadius: '10px',
									padding: '20px',
								}}>
								<Grid container flexDirection={'column'}>
									<Typography
										color={'#5E5C5C'}
										fontSize={'16px'}
										fontWeight={'600'}>
										Recent Summaries
									</Typography>
									<Box
										sx={{
											maxHeight: '680px',
											overflowY: 'auto',
											paddingBottom: '5px',
											paddingX: '10px',
										}}>
										<Grid
											container
											marginTop={'15px'}
											rowGap={1}>
											{summaryData.length > 0 ? (
												<>
													{summaryData.map(
														(item, index) => {
															return (
																<SummaryTile
																	key={index}
																	data={item}
																	onViewSummary={
																		onFetchSummary
																	}
																/>
															);
														},
													)}
												</>
											) : (
												<Grid
													container
													justifyContent={'center'}
													alignItems={'center'}
													marginTop={'50px'}>
													<Typography
														sx={{
															whiteSpace:
																'normal',
															wordBreak:
																'break-word',
															padding: '10px',
															fontSize: '14px',
															color: '#3D4A58',
														}}
														fontWeight={'500'}>
														No Data
													</Typography>
												</Grid>
											)}
										</Grid>
									</Box>
								</Grid>
							</Grid>
						)}
					</Grid>
				) : (
					<SummarizerSection
						data={summarizedData}
						onEdit={handleSummaryChange}
						isEditable={isEditing}
						onGeneratePdf={onGeneratePdf}
						onEnableEdit={onEnableEdit}
						onCloseAll={onCloseAll}
						onSaveSummaries={onSaveSummaries}
						summaryCreatedDate={null}
						isLoading={isStoringData}
						isSummaryEmpty={isSummaryEmpty}
						summaryTemplate={selectedTemplate}
						elder={id}
						onSwitchTemplate={() => {
							setIsSummaryGenerated(false);
							setShowSummaryTemplates(true);
						}}
						isResidentSummary={summaryTypeTab === TABS.rs}
					/>
				)}
			</MainGrid>
		);
	}
}

const StyledTabs = styled(props => (
	<Tabs
		{...props}
		TabIndicatorProps={{
			children: <span className="MuiTabs-indicatorSpan" />,
		}}
	/>
))({
	'& .MuiTabs-indicator': {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: 'transparent',
	},
	'& .MuiTabs-indicatorSpan': {
		width: '100%',
		backgroundColor: MAIN_THEME_COLOR,
	},
});

const StyledTab = styled(props => <Tab {...props} />)(({ theme }) => ({
	textTransform: 'none',
	fontWeight: '500',
	fontSize: theme.typography.pxToRem(15),
	marginRight: theme.spacing(1),
	color: '#868DAA',
	'&.Mui-selected': {
		color: MAIN_THEME_COLOR,
	},
	'&.Mui-focusVisible': {
		backgroundColor: MAIN_THEME_COLOR,
	},
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
	paddingTop: '0px',
	paddingBottom: '0px',
	color: '#868DAA',
	fontSize: '12px',
}));

export default DetailSummarizer;
